import React, { useState } from "react";
import { Card, Button, Row, Col, Typography, Tag, Input } from "antd";
import { DownloadOutlined, FileOutlined, DatabaseOutlined, SearchOutlined, CloudOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

interface Dataset {
  id: number;
  name: string;
  description: string;
  size: string;
  format: string;
  downloadUrl: string;
  category: string;
}

const categoryColors: Record<string, string> = {
  "Şehircilik": "#10b981",      
  "Su Kaynakları": "#3b82f6",   
  "Tarım": "#f59e0b",           
  "Jeoloji": "#ef4444",         
  "İklim": "#8b5cf6",          
  "Çevre": "#22c55e",           
  "Demografi": "#06b6d4",       
};

export const DatasetsSection = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const datasets: Dataset[] = [
    {
      id: 1,
      name: "Acil Durum Toplanma Alanları",
      description: "Konya ilindeki acil durum toplanma alanlarının coğrafi verileri",
      size: "1,7 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/acil-durum-toplanma-alanlari.geojson",
      category: "Şehircilik"
    },
    {
      id: 2,
      name: "Akifer Sınırları",
      description: "Yeraltı suyu akifer sınırları ve özellikleri",
      size: "533,2 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/akifer-sinirlari.geojson",
      category: "Su Kaynakları"
    },
    {
      id: 3,
      name: "Baraj ve Göletler",
      description: "Baraj ve göletlerin lokasyon ve kapasite bilgileri",
      size: "649,8 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/baraj-ve-goletler.geojson",
      category: "Su Kaynakları"
    },
    {
      id: 4,
      name: "Büyük Toprak Grubu",
      description: "Toprak türleri ve özellikleri haritası",
      size: "43,3 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/buyuk-toprak-grubu.geojson",
      category: "Tarım"
    },
    {
      id: 5,
      name: "Diri Faylar",
      description: "Aktif fay hatları ve risk bölgeleri",
      size: "19,4 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/diri-faylar.geojson",
      category: "Jeoloji"
    },
    {
      id: 6,
      name: "Havza Sınırları",
      description: "Su havzalarının sınır bilgileri",
      size: "6 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/havza-sinirlari.geojson",
      category: "Su Kaynakları"
    },
    {
      id: 7,
      name: "İçme Suyu Kaynakları",
      description: "İçme suyu kaynaklarının lokasyonları",
      size: "7,6 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/icme-suyu-kaynaklari.geojson",
      category: "Su Kaynakları"
    },
    {
      id: 8,
      name: "Kuraklık SPI İndeksi",
      description: "Standart yağış indeksi ve kuraklık verileri",
      size: "88 KB",
      format: "GeoJSON",
      downloadUrl: "/datasets/kuraklik-spi-indeksi.geojson",
      category: "İklim"
    },
    {
      id: 9,
      name: "Milli Parklar",
      description: "Korunan alanlar ve milli park sınırları",
      size: "6,8 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/milli-parklar.geojson",
      category: "Çevre"
    },
    {
      id: 10,
      name: "Nüfus Gridli 2024",
      description: "Grid bazlı nüfus dağılım verileri",
      size: "1,2 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/nufus-gridli-2024.geojson",
      category: "Demografi"
    },
    {
      id: 11,
      name: "Nüfus Yoğunluk Gridli 2021",
      description: "2021 yılı nüfus yoğunluk dağılım verileri",
      size: "923,8 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/nufus-yogunluk-gridli-2021.geojson",
      category: "Demografi"
    },
    {
      id: 12,
      name: "Şehir Atlası 2019",
      description: "Şehir detaylı harita ve yapı bilgileri",
      size: "2,1 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/sehir-atlasi-2019.geojson",
      category: "Şehircilik"
    },
    {
      id: 13,
      name: "Sulak Alanlar",
      description: "Sulak alan lokasyonları ve özellikleri",
      size: "1,8 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/sulak-alanlar.geojson",
      category: "Su Kaynakları"
    },
    {
      id: 14,
      name: "Tabiat Parkları",
      description: "Tabiat parkları sınırları ve bilgileri",
      size: "3,8 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/tabiat-parklari.geojson",
      category: "Çevre"
    },
    {
      id: 15,
      name: "Tarım İşletme Sınırları",
      description: "Tarımsal işletme alanları ve sınırları",
      size: "722 KB",
      format: "GeoJSON",
      downloadUrl: "/datasets/tarim-isletme-sinirlari.geojson",
      category: "Tarım"
    },
    {
      id: 16,
      name: "Yeraltı Su Kütlesi",
      description: "Yeraltı su havzaları ve kuyu lokasyonları",
      size: "42,2 MB",
      format: "GeoJSON",
      downloadUrl: "/datasets/yeralti-su-kutlesi.geojson",
      category: "Su Kaynakları"
    }
  ];

  const filteredDatasets = datasets.filter(dataset =>
    dataset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dataset.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDownload = (dataset: Dataset) => {
    const link = document.createElement('a');
    link.href = dataset.downloadUrl;
    link.download = `${dataset.name.toLowerCase().replace(/\s+/g, '-')}.geojson`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section id="datasets" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white px-5 py-2 rounded-lg mb-4 border border-gray-200 shadow-sm">
            <DatabaseOutlined style={{ fontSize: 18, color: "#1f2937" }} />
            <span className="text-xs font-semibold text-gray-700 tracking-wide uppercase">Veri Setleri</span>
          </div>
          
          <Title level={1} className="text-4xl sm:text-5xl font-bold mb-4 text-gray-900">
            CBS Veri Setleri
          </Title>
          
          <Paragraph className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Hackathon'da kullanabileceğiniz <span className="font-semibold text-gray-900">{datasets.length}</span> farklı CBS veri seti
          </Paragraph>

          <div className="max-w-xl mx-auto">
            <Input
              size="large"
              placeholder="Veri seti ara..."
              prefix={<SearchOutlined style={{ color: "#6b7280" }} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded-lg shadow-sm"
              style={{ 
                padding: "10px 16px",
                borderColor: "#d1d5db"
              }}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 mb-10 border border-gray-200 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center">
              <FileOutlined style={{ fontSize: 24, color: "white" }} />
            </div>
            <div className="flex-1">
              <Title level={4} className="mb-2 text-gray-900 font-semibold">
                Veri Setleri Hakkında
              </Title>
              <Paragraph className="text-gray-600 mb-3 leading-relaxed">
                Bu veri setleri T.C. Sanayi ve Teknoloji Bakanlığı, T.C. Çevre, Şehircilik ve İklim Değişikliği Bakanlığı tarafından hackathon katılımcılarına özel olarak sağlanmıştır.
              </Paragraph>
              <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
                <span>• Tüm veriler GeoJSON formatında</span>
                <span>• Yarışma süresince erişilebilir</span>
                <a 
                  href="https://drive.google.com/drive/folders/1AsyapxoTIenmyGNrhE66CExOwhCqHoVt?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer no-underline"
                  style={{ textDecoration: 'none' }}
                >
                  • Google Drive ile kolay erişim
                </a>
              </div>     
            </div>
          </div>
        </div>

        <Row gutter={[20, 20]}>
          {filteredDatasets.map((dataset) => {
            const borderColor = categoryColors[dataset.category] || "#3b82f6";
            const isHovered = hoveredCard === dataset.id;
            
            return (
              <Col xs={24} sm={12} lg={8} key={dataset.id}>
                <Card
                  hoverable
                  className="h-full overflow-hidden"
                  style={{
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    borderTop: "none",
                    boxShadow: isHovered 
                      ? "0 10px 25px -5px rgba(0, 0, 0, 0.1)" 
                      : "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                    transform: isHovered ? "translateY(-4px)" : "translateY(0)",
                  }}
                  styles={{
                    body: { padding: 0, height: "100%" }
                  }}
                  onMouseEnter={() => setHoveredCard(dataset.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div 
                    style={{ 
                      height: "4px", 
                      background: borderColor,
                      borderRadius: "12px 12px 0 0",
                      transition: "all 0.3s ease",
                      width: isHovered ? "100%" : "40%",
                      margin: isHovered ? "0" : "0 auto"
                    }} 
                  />
                  
                  <div className="p-6 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-4">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ 
                          backgroundColor: `${borderColor}15`,
                        }}
                      >
                        <DatabaseOutlined style={{ fontSize: 18, color: borderColor }} />
                      </div>
                      <Tag 
                        className="text-xs px-3 py-1 rounded-full border-0 font-medium"
                        style={{ 
                          backgroundColor: `${borderColor}15`,
                          color: borderColor,
                        }}
                      >
                        {dataset.category}
                      </Tag>
                    </div>

                    <Title level={4} className="mb-2 text-gray-900 font-semibold leading-tight" style={{ fontSize: "16px" }}>
                      {dataset.name}
                    </Title>

                    <Paragraph className="text-gray-500 text-sm mb-5 flex-grow leading-relaxed">
                      {dataset.description}
                    </Paragraph>

                    {/* Alt bilgiler */}
                    <div className="flex items-center gap-6 mb-5 py-3 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <FileOutlined style={{ fontSize: 12, color: "#9ca3af" }} />
                        <Text className="text-xs text-gray-400">{dataset.format}</Text>
                      </div>
                      <div className="flex items-center gap-2">
                        <DatabaseOutlined style={{ fontSize: 12, color: "#9ca3af" }} />
                        <Text className="text-xs text-gray-400">{dataset.size}</Text>
                      </div>
                    </div>

                    <Button
                      icon={<DownloadOutlined />}
                      block
                      size="middle"
                      onClick={() => handleDownload(dataset)}
                      className="font-medium rounded-lg"
                      style={{
                        background: isHovered ? borderColor : "transparent",
                        borderColor: borderColor,
                        color: isHovered ? "white" : borderColor,
                        height: "40px",
                        transition: "all 0.3s ease"
                      }}
                    >
                      İndir
                    </Button>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>

        {filteredDatasets.length === 0 && (
          <div className="text-center py-16">
            <DatabaseOutlined style={{ fontSize: 56, color: "#d1d5db" }} />
            <Title level={3} className="mt-4 text-gray-500">
              Sonuç bulunamadı
            </Title>
            <Text className="text-gray-400">
              Arama kriterlerinizi değiştirerek tekrar deneyin
            </Text>
          </div>
        )}

      </div>
    </section>
  );
};